import type { NotificationDto } from '@pos/api-client';
import { Button, EmptyState, ErrorState, Skeleton, cn } from '@pos/ui';
import { AlertTriangle, Bell, Boxes, CloudOff, Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { Screen } from '../../app/Screen';
import { useNotifications, useReadNotifications } from '../../app/data';
import { ago } from '../../lib/format';

const ICON: Record<string, ReactNode> = {
  'shift.difference': <AlertTriangle className="size-5 text-warning" />,
  'shift.many_removals': <Trash2 className="size-5 text-warning" />,
  'stock.low': <Boxes className="size-5 text-primary" />,
  'sync.rejected': <CloudOff className="size-5 text-danger" />,
};

/** Where a notice leads when tapped, if anywhere. */
function target(n: NotificationDto): string | null {
  if (n.type === 'stock.low' && typeof n.data?.productId === 'string') return `/products/${n.data.productId}`;
  if (n.type === 'shift.difference' || n.type === 'shift.many_removals') return '/more/reports';
  return null;
}

/**
 * What happened that the owner should know: a till short at closing, a lot
 * of removals in a shift, goods running out, a sale the cloud refused.
 */
export function NotificationsPage() {
  const { data, isPending, isError, refetch } = useNotifications();
  const read = useReadNotifications();
  const navigate = useNavigate();
  const unread = data?.unread ?? 0;

  return (
    <Screen
      title="Уведомления"
      back="/more"
      actions={
        unread > 0 ? (
          <Button size="sm" variant="ghost" loading={read.isPending && read.variables === 'all'} onClick={() => read.mutate('all')}>
            Прочитать все
          </Button>
        ) : undefined
      }
    >
      {isError && !data ? (
        <ErrorState description="Не удалось загрузить уведомления." onRetry={() => void refetch()} />
      ) : isPending || !data ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-20 rounded-2xl" />
          ))}
        </div>
      ) : data.items.length === 0 ? (
        <EmptyState
          icon={<Bell />}
          title="Всё спокойно"
          description="Здесь появятся недостачи при закрытии смены, заканчивающиеся товары и продажи, которые облако не приняло."
        />
      ) : (
        <ul className="space-y-2">
          {data.items.map((n) => {
            const to = target(n);
            const fresh = n.readAt === null;
            return (
              <li key={n.id}>
                <button
                  type="button"
                  onClick={() => {
                    if (fresh) read.mutate(n.id);
                    if (to) navigate(to);
                  }}
                  className={cn(
                    'flex w-full gap-3 rounded-2xl border p-4 text-left active:bg-sunken',
                    fresh ? 'border-primary-line bg-surface' : 'border-line bg-surface/60',
                  )}
                >
                  <span className="mt-0.5 shrink-0">{ICON[n.type] ?? <Bell className="size-5 text-ink-3" />}</span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-start gap-2">
                      <span className={cn('min-w-0 flex-1 text-md', fresh ? 'font-semibold' : 'font-medium text-ink-2')}>{n.title}</span>
                      {fresh && <span aria-label="Новое" className="mt-2 size-2 shrink-0 rounded-full bg-primary" />}
                    </span>
                    {n.body && <span className="mt-0.5 block text-base text-ink-2">{n.body}</span>}
                    <span className="mt-1 block text-sm text-ink-3">{ago(n.createdAt)}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Screen>
  );
}
